<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <content-dialog
    :visible.sync="modalVisible"
    max-width="700px"
    :scrollable="true"
  >
    <template slot="dialogTitle">
      <span>
        Set yacht investment agreement details
      </span>
      <div class="body-1">
        <v-row
          no-gutters
          align="center"
          class="list--bordered py-2"
        >
          <v-col>
            {{ financeFirstPayment ? 'First payment' : 'Total payment' }}
          </v-col>
          <v-col
            cols="auto"
            class="font-weight-bold"
          >
            {{ financeFirstPayment || financeTotalPayment | currency(yacht.currency) }}
          </v-col>
        </v-row>
        <v-row
          v-if="financeLastPayment"
          no-gutters
          align="center"
          class="list--bordered py-2"
        >
          <v-col>
            Last payment
          </v-col>
          <v-col
            cols="auto"
            class="font-weight-bold"
          >
            {{ financeLastPayment | currency(yacht.currency) }}
          </v-col>
        </v-row>
      </div>
    </template>

    <v-card-text class="pa-6">
      <v-row
        no-gutters
        class="mb-5"
        align="center"
      >
        <v-col cols="4">
          <v-switch
            v-model="vat.apply"
            dense
            label="Apply VAT"
            hide-details
            class="pa-0 ma-0"
          />
        </v-col>
        <v-col
          v-if="vat.apply"
          cols="4"
        >
          <currency-input
            v-model="vat.rate"
            outlined
            hide-details
            dense
            :precision="0"
            suffix="%"
          />
        </v-col>
        <v-col
          v-if="vat.apply"
          cols="5"
          class="pl-2"
        >
          <v-autocomplete
            :items="countryVatRates"
            item-value="vat_rate"
            outlined
            dense
            hide-details
            label="Select by country"
            @change="vat.rate = Number(Number($event * 100).toFixed(0))"
          >
            <template v-slot:item="data">
              {{ data.item.name }} ({{ (Number(data.item.vat_rate) * 100).toFixed(0) }}%)
            </template>
            <template v-slot:selection="data">
              {{ data.item.name }} ({{ (Number(data.item.vat_rate) * 100).toFixed(0) }}%)
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <!--<editor-fold description="First payment plan">-->

      <validation-observer ref="validationObserver">
        <div v-if="$can('edit-insured-value', 'Yacht')">
          <v-subheader>
            Insurance
          </v-subheader>

          <v-row
            dense
            class="mb-4"
          >
            <v-col cols="6">
              <currency-input
                v-model="insuranceRateToOwner"
                suffix="%"
                :precision="0"
                outlined
                hide-details
                label="Insurance rate to owner (%)"
              />
            </v-col>
          </v-row>

          <v-row
            dense
          >
            <v-col>
              <currency-input
                v-model="insuredValue"
                :suffix="yacht.currency"
                :precision="0"
                outlined
                hide-details
                label="Insured value"
              />
            </v-col>
            <v-col>
              <currency-input
                v-model="insuredRate"
                :suffix="yacht.currency"
                :precision="0"
                outlined
                hide-details
                label="Charged rate"
              />
            </v-col>
            <v-col>
              <v-menu
                v-model="insuranceValidToDatePicker"
                :close-on-content-click="false"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="insuranceValidTo ? $moment(insuranceValidTo).format('DD.MM.YYYY.') : ''"
                    outlined
                    label="Valid to"
                    readonly
                    hide-details
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="insuranceValidTo"
                  no-title
                  :first-day-of-week="1"
                  @input="insuranceValidToDatePicker = false"
                />
              </v-menu>
            </v-col>
          </v-row>
          <v-row
            class="mt-2"
            align="center"
          >
            <v-col cols="auto">
              <file-upload
                ref="upload"
                v-model="insuranceFiles"
                :post-action="uploadUrl"
                multiple
                :data="{ type: 'insurance', yacht_id: yacht.id }"
                :headers="{ 'Authorization': 'Bearer ' + $store.state.auth.token, 'Accept': 'application/json' }"
                @input-file="inputFile"
              >
                <v-btn
                  color="primary"
                  class="text-none ma-0"
                  outlined
                  small
                  :loading="insuranceFiles.some(f => f.active)"
                >
                  <v-icon
                    left
                    small
                  >
                    add
                  </v-icon>
                  Upload insurance policy
                </v-btn>
              </file-upload>
            </v-col>
            <v-col
              v-if="insuranceFiles.some(f => f.file && f.file.id)"
              class="caption"
            >
              Document uploaded. <router-link
                :to="{ name: 'yachtDocuments', params: { id: yacht.id }}"
                @click.stop="modalVisible = false"
              >
                View insurance documents
              </router-link>
            </v-col>
          </v-row>
        </div>

        <v-subheader class="mt-4">
          Financing
        </v-subheader>

        <v-row
          dense
          align="center"
          class="mb-4"
        >
          <v-col cols="6">
            <v-checkbox
              v-model="subjectToFinancing"
              label="Subject to financing"
            />
          </v-col>
          <v-col cols="5">
            <v-menu
              v-model="financingDecisionDatePicker"
              :close-on-content-click="false"
              transition="fade-transition"
              min-width="290px"
              absolute
              nudge-bottom="60px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="financingDecisionDate ? $moment(financingDecisionDate).format('DD.MM.YYYY.') : ''"
                  outlined
                  label="Accept/reject date"
                  readonly
                  hide-details
                  clearable
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="financingDecisionDate"
                no-title
                :first-day-of-week="1"
                @input="financingDecisionDatePicker = false"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-subheader>
          Other agreed sales terms
        </v-subheader>

        <tiny-mce
          id="agreement_details_editor"
          key="agreement_details_editor"
          v-model="specialDeals"
          width="auto"
          simple
        />

        <v-subheader class="mt-4">
          Other agreed operational terms
        </v-subheader>

        <tiny-mce
          id="agreement_details_editor_operational"
          key="agreement_details_editor_operational"
          v-model="operationalTerms"
          width="auto"
          simple
        />
      </validation-observer>
    </v-card-text>

    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="save"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
  </content-dialog>
</template>

<script>
import ContentDialog from '@/components/helpers/Dialog'
import CurrencyInput from '@/components/helpers/CurrencyInput'
import cloneDeep from 'lodash/cloneDeep'
import TinyMce from '@/components/helpers/TinyMce'
import Yacht from '@/models/Yacht'
import VueUploadComponent from 'vue-upload-component'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'

export default {
    components: {
      TinyMce,
      CurrencyInput,
      ContentDialog,
      FileUpload: VueUploadComponent
    },
    props: {
      visible: Boolean,
      yacht: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        agreementDetailsDirty: cloneDeep(Yacht.emptyModel.agreement_details),
        specialDeals: '',
        operationalTerms: '',
        submitting: false,
        payments: [],
        remainingPayment: {
          dueDate: null,
          datePicker: false
        },
        insuredValue: null,
        insuredRate: null,
        insuranceValidTo: null,
        insuranceValidToDatePicker: false,
        subjectToFinancing: false,
        financingDecisionDate: null,
        financingDecisionDatePicker: false,
        insuranceFiles: [],
        purchasePayments: [],
        remainingPurchasePayment: {
          dueDate: null,
          datePicker: false
        },
        vat: {
          apply: false,
          rate: 0
        },
        lastPayment: {
          dueDate: null,
          datePicker: false
        },
        operationalFee: null,
        insuranceRateToOwner: null
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      financeFirstPayment() {
        if (!this.yacht.finance_model_calculation) return false

        return this.yacht.finance_model_calculation.investor.first_payment || false
      },
      financeLastPayment() {
        if (!this.yacht.finance_model_calculation) return false

        const payment = this.yacht.finance_model_calculation.investor.last_payment

        return payment ? payment.toFixed(0) : false
      },
      financeTotalPayment() {
        if (!this.yacht.finance_model_calculation || this.financeFirstPayment) return false

        return this.yacht.finance_model_calculation.investor.sale_price
      },
      financePayment() {
        let amount = this.financeFirstPayment || this.financeTotalPayment

        if (this.vat.apply && this.vat.rate > 0) {
          amount = amount * ((Number(this.vat.rate + 100) / 100).toFixed(2))
        }

        return Number(amount).toFixed(0)
      },
      paymentAmount() {
        if (this.payments.length === 0) return 0

        return this.payments.map(payment => Number(payment.amount)).reduce((a, b) => a + b)
      },
      remainingPaymentAmount() {
        return Number(this.financePayment) - Number(this.paymentAmount)
      },
      totalPurchasePaymentAmount() {
        let amount = Number(this.yacht.base_purchase_price) + Number(this.yacht.equipment_packs
          .filter(ep => ep.installed_by === YachtEquipmentPack.INSTALLED_BY_SHIPYARD)
          .map(ep => ep.pivot_purchase_price)
          .reduce((a, b) => Number(a) + Number(b)))

        if (this.vat.apply && this.vat.rate > 0) {
          amount = amount * ((Number(this.vat.rate + 100) / 100).toFixed(2))
        }

        return Number(amount).toFixed(0)
      },
      purchasePaymentAmount() {
        if (this.purchasePayments.length === 0) return 0

        return this.purchasePayments.map(payment => Number(payment.amount)).reduce((a, b) => a + b)
      },
      remainingPurchasePaymentAmount() {
        return Number(this.totalPurchasePaymentAmount) - Number(this.purchasePaymentAmount)
      },
      countries() {
        return this.$store.getters['settings/countries'].data.filter(c => c.vat_rates && c.vat_rates.length > 0)
      },
      countryVatRates() {
        const vatRates = []

        for (let country of this.countries) {
          for (let vatRate of country.vat_rates) {
            vatRates.push({
              ...country,
              vat_rate: vatRate
            })
          }
        }

        return vatRates
      }
    },
    watch: {
      yacht: function (yacht) {
        this.loadCurrentAgreementDetails()
      }
    },
    created() {
      this.loadCurrentAgreementDetails()

      this.uploadUrl = process.env.VUE_APP_API_URL + '/documents/upload'
    },
    methods: {
      loadCurrentAgreementDetails() {
        if (this.yacht.agreement_details) {
          this.specialDeals = this.yacht.agreement_details.special_deals
          this.operationalTerms = this.yacht.agreement_details.operational_terms

          if (this.yacht.agreement_details.insured_value) {
            this.insuredValue = this.yacht.agreement_details.insured_value
          } else {
            this.insuredValue = Number(this.yacht.total_sale_price_final)
          }

          this.subjectToFinancing = this.yacht.agreement_details.subject_to_financing || false
          this.financingDecisionDate = this.yacht.agreement_details.financing_decision_date || null

          if (this.yacht.agreement_details.insured_rate) this.insuredRate = this.yacht.agreement_details.insured_rate
          if (this.yacht.agreement_details.insurance_valid_to) this.insuranceValidTo = this.yacht.agreement_details.insurance_valid_to
          if (this.yacht.agreement_details.operational_fee) this.operationalFee = this.yacht.agreement_details.operational_fee
          if (this.yacht.agreement_details.insurance_rate_to_owner) this.insuranceRateToOwner = this.yacht.agreement_details.insurance_rate_to_owner
        }

        if (this.yacht.ex_factory_date) {
          this.remainingPayment.dueDate = this.$moment(this.yacht.ex_factory_date).subtract(30, 'days').format('YYYY-MM-DD')
        }

        if (this.yacht.phase_out_date) {
          this.lastPayment.dueDate = this.$moment(this.yacht.phase_out_date).format('YYYY-MM-DD')
        }

        if (!this.yacht.agreement_details) return
        if (!this.yacht.agreement_details.first_payment) return
        if (!this.yacht.agreement_details.first_payment.payments) return
        if (this.yacht.agreement_details.first_payment.payments.length === 0) return

        const payments = cloneDeep(this.yacht.agreement_details.first_payment.payments)

        const remainingPayment = payments.pop()

        this.remainingPayment.dueDate = remainingPayment.due_date

        this.payments = payments.map(p => {
          return {
            amount: p.amount,
            dueDate: p.due_date,
            isDeposit: p.is_deposit,
            datePicker: false
          }
        })

        const purchasePayments = this.yacht.agreement_details && this.yacht.agreement_details.purchase_payments ? cloneDeep(this.yacht.agreement_details.purchase_payments) : []

        const remainingPurchasePayment = purchasePayments.pop()

        if (remainingPurchasePayment) {
          this.remainingPurchasePayment.dueDate = remainingPurchasePayment.due_date
        } else {
          this.remainingPurchasePayment.dueDate = null
        }

        if (this.yacht.agreement_details && this.yacht.agreement_details.last_payment) {
          this.lastPayment.dueDate = this.yacht.agreement_details.last_payment.due_date
        }

        this.purchasePayments = purchasePayments.map(p => {
          return {
            amount: p.amount,
            dueDate: p.due_date,
            isDeposit: p.is_deposit,
            datePicker: false
          }
        })

        if (this.yacht.agreement_details.vat_rate) {
          this.vat.apply = true
          this.vat.rate = Number(this.yacht.agreement_details.vat_rate)
        }
      },
      async save() {
        let validation = await this.$refs.validationObserver.validate()

        if (!validation) return

        try {
          this.submitting = true

          let data = {
            special_deals: this.specialDeals,
            operational_terms: this.operationalTerms,
            insured_value: this.insuredValue,
            insured_rate: this.insuredRate,
            insurance_valid_to: this.insuranceValidTo,
            subject_to_financing: this.subjectToFinancing,
            financing_decision_date: this.financingDecisionDate,
            operational_fee: this.operationalFee,
            insurance_rate_to_owner: this.insuranceRateToOwner,
            first_payment: {},
            purchase_payments: this.purchasePayments.map(p => {
              return {
                amount: p.amount,
                due_date: p.dueDate,
                is_deposit: p.isDeposit
              }
            }),
            last_payment: this.financeLastPayment && this.lastPayment.dueDate ? {
              amount: Number(this.financeLastPayment),
              due_date: this.lastPayment.dueDate,
              is_deposit: false
            } : undefined,
            vat_rate: this.vat.apply && this.vat.rate > 0 ? this.vat.rate : null
          }

          if (this.payments.length > 0) {
            data.first_payment.payments = this.payments.map(p => {
              return {
                amount: p.amount,
                due_date: p.dueDate,
                is_deposit: p.isDeposit
              }
            })
          } else {
            data.first_payment.payments = []
          }

          if (this.remainingPayment.dueDate) {
            data.first_payment.payments.push({
              amount: this.remainingPaymentAmount,
              due_date: this.remainingPayment.dueDate,
              is_deposit: false
            })
          }

          if (this.remainingPurchasePayment.dueDate) {
            data.purchase_payments.push({
              amount: this.remainingPurchasePaymentAmount,
              due_date: this.remainingPurchasePayment.dueDate,
              is_deposit: false
            })
          }

          let result = await Yacht.api.storeAgreementDetails(this.yacht.id, data)

          this.$store.dispatch('alert', {
            text: 'Agreement details saved.',
            color: 'success'
          })

          this.$emit('update:yacht', result.yacht)

          if (result.payments_created) {
            this.$store.dispatch('alert', {
              text: 'Cash flow plan generated. Please check under Finance tab.',
              color: 'success'
            })

            if (this.$can('view-finance-details', 'Yacht')) {
              this.$router.push({ name: 'yachtFinanceDetails', params: { id: this.yacht.id } })
            }
          }

          this.modalVisible = false
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong!',
            color: 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      addPayment() {
        this.payments.push({
          amount: null,
          dueDate: null,
          isDeposit: true,
          datePicker: false
        })
      },
      removePayment(i) {
        this.payments.splice(i, 1)
      },
      addPurchasePayment() {
        this.purchasePayments.push({
          amount: null,
          dueDate: null,
          isDeposit: true,
          datePicker: false
        })
      },
      removePurchasePayment(i) {
        this.purchasePayments.splice(i, 1)
      },
      inputFile(newFile, oldFile) {
        if (newFile && !oldFile) {
          this.$refs.upload.update(newFile, { active: true })
          return
        }

        if (newFile && oldFile && !newFile.active && oldFile.active) {
          // Get response data

          this.$refs.upload.remove(newFile)
          this.insuranceFiles.unshift(newFile.response)
        }
      }
    }
  }
</script>
